import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useLatest from 'react-use/lib/useLatest';

type ContentNavigationHash = {
  sectionFromHash: string | undefined;
  synchronizeSectionHash: (id: string) => void;
};

const getSectionFromHash = (hash: string): string | undefined => {
  if (!hash) return undefined;

  const encodedSection = hash.slice(1);

  try {
    return decodeURIComponent(encodedSection);
  } catch {
    return encodedSection;
  }
};

export const useContentNavigationHash = (): ContentNavigationHash => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useLatest(location);
  const synchronizeSectionHash = useCallback(
    (id: string): void => {
      const {
        hash: currentHash,
        pathname,
        search,
        state,
      } = locationRef.current;
      if (getSectionFromHash(currentHash) === id) return;

      const nextHash = `#${encodeURIComponent(id)}`;
      navigate({ pathname, search, hash: nextHash }, { replace: true, state });
    },
    [locationRef, navigate],
  );

  return {
    sectionFromHash: getSectionFromHash(location.hash),
    synchronizeSectionHash,
  };
};
